import { createClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';

export const sanityClient = createClient({
  projectId: process.env.NEXT_PUBLIC_SANITY_PROJECT_ID  || '2azshrlg', 
  dataset: 'production', 
  apiVersion: '2024-01-01', 
  useCdn: true, 
});

const builder = imageUrlBuilder(sanityClient);
export function urlFor(source) {
  return builder.image(source);
}

// [0] ensures we only get the first document found (since we only have one profile)
export const profileQuery = `*[_type == "profile"][0] {
  name,
  title,
  tagline,
  summary,
  bio,
  email,
  github,
  linkedin,
  location,
  "avatarUrl": avatar.asset->url,
  nowUpdated,
  nowItems,
  funFacts
}`;

// 1. Fetch all Experiences, ordered by period
export const experiencesQuery = `*[_type == "experience"] | order(period.from desc) {
  _id,
  title,
  company,
  location,
  period,
  description,
  technologies
}`;

// 2. Fetch all Projects
export const projectsQuery = `*[_type == "project"] | order(orderRank asc) {
  _id,
  title,
  description,
  technologies,
  github,
  demo,
  "imageUrl": image.asset->url, // Fetches the actual URL for the Sanity image asset
  "galleryUrls": gallery[].asset->url,
  "blogSlug": blogPost->slug.current,
}`;

// 3. Fetch all Education records
export const educationQuery = `*[_type == "education"] | order(period.from desc) {
  _id,
  degree,
  school,
  location,
  period,
  gpa,
  relevant,
  description
}`;

// 4. Fetch the single Skills document
export const skillsQuery = `*[_type == "skills"][0] {
  languages,
  frameworks,
  tools,
  interests
}`;

// 5. Fetch Resume PDF (singleton)
export const resumeQuery = `*[_type == "resume"][0] {
  title,
  pdfFile,
  lastUpdated,
  "pdfUrl": pdfFile.asset->url
}`;

export async function getProfile() {
  return await sanityClient.fetch(profileQuery);
}

export async function getExperiences() {
  return await sanityClient.fetch(experiencesQuery);
}

export async function getProjects() {
  return await sanityClient.fetch(projectsQuery);
}

export async function getEducation() {
  return await sanityClient.fetch(educationQuery);
}

// 5. Fetch all Blog Posts (slug required — a post without one would
// otherwise crash the /blog list, which links via slug.current)
export const blogPostsQuery = `*[_type == "blogPost" && defined(slug.current)] | order(publishedAt desc) {
  _id,
  title,
  slug,
  publishedAt,
  excerpt,
  content,
  tags
}`;

export const artPhotosQuery = `*[_type == "artPhoto"] | order(capturedAt desc) {
  _id,
  title,
  category,
  capturedAt,
  location,
  description,
  artDescription,
  tags,
  altText,
  "imageUrl": image.asset->url
}`;

export async function getSkills() {
  return await sanityClient.fetch(skillsQuery);
}

export async function getResume() {
  return await sanityClient.fetch(resumeQuery);
}

export async function getBlogPosts() {
  return await sanityClient.fetch(blogPostsQuery);
}

export async function getArtPhotos() {
  return await sanityClient.fetch(artPhotosQuery);
}

// Microblog "lab log" entries, newest first.
export const microblogsQuery = `*[_type == "microblog"] | order(postedAt desc) {
  _id,
  text,
  postedAt,
  mood,
  tags,
  link
}`;

export async function getMicroblogs() {
  return await sanityClient.fetch(microblogsQuery);
}

// Fetch a single blog post by slug
export async function getBlogPostBySlug(slug) {
  const query = `*[_type == "blogPost" && slug.current == $slug][0] {
    _id,
    title,
    slug,
    publishedAt,
    excerpt,
    content[]{
      ...,
      _type == "pdf" => { "url": asset->url }
    },
    tags
  }`;
  return await sanityClient.fetch(query, { slug });
}
